import * as React from "react";
import { ChevronDown, Palette } from "lucide-react";
import {
  Button,
  confirmDialog,
  Dialog,
  DropdownMenu,
  Field,
  RadioGroup,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  Textarea,
} from "@/components/ui";
import { HoloShimmer } from "@/components/Holo";
import { formatRelativeTime } from "@/components/reviews/utils";
import { useRenderGroup } from "@/components/ShareDesignPost/hooks/useRenderGroup";
import { DesignPackageDetail, DesignPackageItem } from "@/services/bedrock/dtos/DesignPackage";
import { useEditorThemes } from "../hooks/useEditorThemes";
import { usePreviews } from "../hooks/usePreviews";
import { useSourceMutations } from "../hooks/useSourceMutations";
import { useTemplateSource } from "../hooks/useTemplateSource";
import { isItemInProgress } from "../slideState";
import { CodeEditor } from "./CodeEditor";
import { DisabledReason } from "./DisabledReason";
import { FitFrame } from "./FitFrame";
import { ThemeSwatches } from "./ThemeSwitcher";

interface SourcePanelProps {
  pkg: DesignPackageDetail;
  /** The slide in view on the stage; the sheet follows it. */
  item: DesignPackageItem | undefined;
  /** The stage's selected theme; the sheet starts on it. */
  themeId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Version = "current" | "candidate";

const MAX_NOTE_LENGTH = 500;
const DEFAULT_THEME_LABEL = "Default branding";

/** Unsaved edits are only ever thrown away after this prompt. */
export function confirmDiscardEdits(): Promise<boolean> {
  return confirmDialog({
    title: "Discard your edits?",
    description: "Your changes to this slide's code haven't been applied.",
    confirmLabel: "Discard",
    cancelLabel: "Keep editing",
    destructive: true,
  });
}

/** The version the sheet opens on: the new version when the slide has one waiting, else the current one. */
export function defaultVersionFor(item: Pick<DesignPackageItem, "candidateTemplate"> | undefined): Version {
  return item?.candidateTemplate ? "candidate" : "current";
}

function isSubmitShortcut(event: React.KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && (event.key === "Enter" || event.key.toLowerCase() === "s");
}

/**
 * An admin's view into a slide's template code: read it, edit it, render real test images through
 * Darkroom, and apply the result as a new version for the coach to keep or discard, or approved at
 * once. Playing around writes nothing; only Apply does.
 */
export const SourcePanel: React.FC<SourcePanelProps> = ({ pkg, item: itemInView, themeId, open, onOpenChange }) => {
  const [itemId, setItemId] = React.useState<number | null>(itemInView?.id ?? null);
  const [version, setVersion] = React.useState<Version>(() => defaultVersionFor(itemInView));
  // null = untouched: the editor shows the loaded source
  const [edited, setEdited] = React.useState<string | null>(null);
  const [draftGroupId, setDraftGroupId] = React.useState<number | null>(null);
  const [sheetThemeId, setSheetThemeId] = React.useState<number | null>(themeId);
  const [applyOpen, setApplyOpen] = React.useState(false);
  const [approveNow, setApproveNow] = React.useState(false);
  const [note, setNote] = React.useState("");
  const prompting = React.useRef(false);
  // The slide in view the admin chose to keep editing over; asked once, not on every keystroke
  const declinedFollow = React.useRef<number | null>(null);

  const items = React.useMemo(() => [...pkg.items].sort((a, b) => a.position - b.position), [pkg.items]);
  const item = items.find(i => i.id === itemId) ?? itemInView;
  const template = version === "candidate" && item?.candidateTemplate ? item.candidateTemplate : item?.activeTemplate;
  const templateId = template?.id ?? null;
  // The version being edited was kept or discarded by the coach meanwhile: the edits stay, now
  // against the version that took its place, and the sheet says so
  const [rebased, setRebased] = React.useState(false);
  const editedTemplateId = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (edited == null) {
      editedTemplateId.current = null;
      setRebased(false);
      return;
    }
    if (editedTemplateId.current == null) editedTemplateId.current = templateId;
    else if (editedTemplateId.current !== templateId) {
      editedTemplateId.current = templateId;
      setRebased(true);
      setDraftGroupId(null);
    }
  }, [edited, templateId]);

  const {
    data: source,
    isLoading: sourceLoading,
    error: sourceError,
  } = useTemplateSource(templateId, {
    enabled: open,
  });
  const { options: themeOptions } = useEditorThemes();
  const { slideFor } = usePreviews(pkg.id, sheetThemeId);
  const { group: draft, isInFlight: draftInFlight, isFailed: draftFailed } = useRenderGroup(draftGroupId);
  const { testRender, apply } = useSourceMutations(pkg.id);

  const value = edited ?? source?.html ?? "";
  const dirty = edited != null && source != null && edited !== source.html;
  const blank = value.trim().length === 0;

  // Each opening starts over on the slide in view, its default version, and the stage's theme;
  // closing forgets the edits (they were kept or discarded by then) so nothing stale carries over.
  const wasOpen = React.useRef(open);
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setItemId(itemInView?.id ?? null);
      setVersion(defaultVersionFor(itemInView));
      setSheetThemeId(themeId);
    }
    if (open !== wasOpen.current) {
      setEdited(null);
      setDraftGroupId(null);
      declinedFollow.current = null;
    }
    wasOpen.current = open;
  }, [open, itemInView, themeId]);

  // Once the edits are gone (Reset, Apply), the sheet may follow the slide in view again
  React.useEffect(() => {
    if (!dirty) declinedFollow.current = null;
  }, [dirty]);

  // The sheet follows the slide in view, but never over unsaved edits without asking.
  const inViewId = itemInView?.id ?? null;
  React.useEffect(() => {
    if (!open || inViewId == null || inViewId === itemId || prompting.current) return;
    if (declinedFollow.current === inViewId) return;
    const follow = () => {
      declinedFollow.current = null;
      setItemId(inViewId);
      setVersion(defaultVersionFor(items.find(i => i.id === inViewId)));
      setEdited(null);
      setDraftGroupId(null);
    };
    if (!dirty) {
      follow();
      return;
    }
    prompting.current = true;
    void confirmDiscardEdits().then(discard => {
      prompting.current = false;
      if (discard) follow();
      else declinedFollow.current = inViewId;
    });
  }, [open, inViewId, itemId, dirty, items]);

  const requestClose = async () => {
    if (dirty && !(await confirmDiscardEdits())) return;
    onOpenChange(false);
  };

  const switchVersion = async (next: Version) => {
    if (next === version) return;
    if (dirty && !(await confirmDiscardEdits())) return;
    setVersion(next);
    setEdited(null);
    setDraftGroupId(null);
  };

  const renderTest = () => {
    if (templateId == null || blank || testRender.isPending) return;
    testRender.mutate(
      { templateId, body: { html: value, themeId: sheetThemeId } },
      { onSuccess: group => setDraftGroupId(group.id) }
    );
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isSubmitShortcut(event)) return;
    event.preventDefault();
    renderTest();
  };

  const openApply = () => {
    setApproveNow(pkg.isGlobal);
    setNote("");
    setApplyOpen(true);
  };

  const confirmApply = () => {
    if (!item || blank) return;
    apply.mutate(
      {
        itemId: item.id,
        body: {
          html: value,
          approve: pkg.isGlobal || approveNow,
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(templateId != null ? { baseDesignTemplateId: templateId } : {}),
        },
      },
      {
        onSuccess: () => {
          setApplyOpen(false);
          setEdited(null);
          onOpenChange(false);
        },
      }
    );
  };

  // The server refuses while any slide of the design is in progress, not just this one
  const inProgress = !!pkg.inFlight || items.some(isItemInProgress) || (!!item && isItemInProgress(item));
  const applyReason = inProgress ? "Wait for the current change to finish." : null;
  const rendering = testRender.isPending || (draftGroupId != null && draftInFlight);
  const slidePosition = item?.position ?? 0;
  const sheetTheme = themeOptions.find(o => o.theme.id === sheetThemeId)?.theme ?? null;
  const currentImage = item ? slideFor(item.id).url : null;
  const draftImage = draft?.status === "success" ? draft.slideUrls[0] ?? null : null;

  const renderImage = () => {
    if (draftGroupId != null) {
      if (draftFailed) {
        return (
          <div
            className="flex size-full items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-destructive"
            data-testid="source-render-failed"
          >
            Couldn't render this version. Check the code and try again.
          </div>
        );
      }
      if (!draftImage) {
        return <HoloShimmer aspectRatio={pkg.aspectRatio} label="Rendering…" className="size-full rounded-xl" />;
      }
      return (
        <div className="size-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
          <img src={draftImage} alt="Test render" className="size-full object-cover" />
        </div>
      );
    }
    return (
      <div className="size-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
        {currentImage && <img src={currentImage} alt="Current preview" className="size-full object-cover" />}
      </div>
    );
  };

  return (
    <>
      <Sheet open={open} onOpenChange={next => (next ? onOpenChange(true) : void requestClose())}>
        <SheetContent
          side="right"
          // The base sheet caps right-hand sheets at sm:max-w-sm under the same data-side variant, so the
          // override has to carry that variant to win
          className="gap-0 overflow-hidden data-[side=right]:w-[min(96vw,1400px)] data-[side=right]:sm:max-w-none"
          data-testid="source-panel"
        >
          <SheetHeader className="shrink-0 gap-2 border-b border-slate-200 pr-12">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex flex-col gap-0.5">
                <SheetTitle>Source</SheetTitle>
                <p className="text-xs text-slate-500">
                  Slide {slidePosition} of {items.length}
                </p>
              </div>
              {item?.candidateTemplate && (
                <Tabs value={version} onValueChange={next => void switchVersion(next as Version)}>
                  <Tabs.List aria-label="Version">
                    <Tabs.Trigger value="current">Current version</Tabs.Trigger>
                    <Tabs.Trigger value="candidate">New version</Tabs.Trigger>
                  </Tabs.List>
                </Tabs>
              )}
            </div>
          </SheetHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:flex-row lg:overflow-hidden">
            <section className="flex min-h-[50dvh] min-w-0 flex-1 flex-col gap-2 lg:min-h-0 lg:flex-[3]">
              <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                <span>{source?.isActive === false ? "Not the active version" : "Active version"}</span>
                {dirty && (
                  <Button variant="ghost" size="sm" onClick={() => setEdited(null)}>
                    Reset
                  </Button>
                )}
              </div>
              {rebased && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="source-rebased">
                  The version you were editing was kept or discarded. Your edits now apply to the current version.
                </p>
              )}
              {sourceError ? (
                <p className="text-sm text-destructive">We couldn't load this slide's code.</p>
              ) : sourceLoading || !source ? (
                <div className="flex min-h-0 flex-1 flex-col" data-testid="source-loading">
                  <HoloShimmer aspectRatio="landscape_4_3" showLogo={false} className="w-full flex-1 rounded-lg" />
                </div>
              ) : (
                <CodeEditor
                  value={value}
                  onChange={setEdited}
                  onKeyDown={onKeyDown}
                  aria-label="Template code"
                  className="min-h-0 flex-1"
                />
              )}
            </section>

            <aside className="flex min-w-0 flex-col gap-3 lg:min-h-0 lg:flex-[2]">
              <FitFrame aspectRatio={pkg.aspectRatio} className="mx-auto max-w-[520px]">
                {renderImage()}
              </FitFrame>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <DropdownMenu>
                  <DropdownMenu.Trigger
                    render={props => (
                      <Button
                        {...props}
                        variant="ghost"
                        size="sm"
                        aria-label={`Theme: ${sheetTheme?.name ?? DEFAULT_THEME_LABEL}`}
                        className="max-w-60 gap-1.5 px-2"
                      >
                        {sheetTheme ? (
                          <ThemeSwatches theme={sheetTheme} />
                        ) : (
                          <Palette className="size-3.5 text-slate-500" aria-hidden="true" />
                        )}
                        <span className="truncate">{sheetTheme?.name ?? DEFAULT_THEME_LABEL}</span>
                        <ChevronDown className="size-3.5 text-slate-400" aria-hidden="true" />
                      </Button>
                    )}
                  />
                  <DropdownMenu.Content align="start" className="max-h-80 min-w-60">
                    <DropdownMenu.RadioGroup
                      value={sheetThemeId == null ? "default" : String(sheetThemeId)}
                      onValueChange={key => setSheetThemeId(key === "default" ? null : Number(key))}
                    >
                      <DropdownMenu.RadioItem value="default" closeOnClick>
                        {DEFAULT_THEME_LABEL}
                      </DropdownMenu.RadioItem>
                      {themeOptions.map(option => (
                        <DropdownMenu.RadioItem key={option.theme.id} value={String(option.theme.id)} closeOnClick>
                          <ThemeSwatches theme={option.theme} />
                          {option.label}
                        </DropdownMenu.RadioItem>
                      ))}
                    </DropdownMenu.RadioGroup>
                  </DropdownMenu.Content>
                </DropdownMenu>
                {draftImage && draft && (
                  <span className="text-xs text-slate-500">Rendered {formatRelativeTime(draft.updatedAt)}</span>
                )}
              </div>
            </aside>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 p-4">
            <Button variant="secondary" disabled={blank || rendering || !source} onClick={renderTest}>
              {rendering ? "Rendering…" : "Render test"}
            </Button>
            <DisabledReason reason={applyReason} data-testid="apply-wrapper">
              <Button variant="default" disabled={blank || !source || !!applyReason} onClick={openApply}>
                Apply…
              </Button>
            </DisabledReason>
          </div>
        </SheetContent>
      </Sheet>

      {/* Nothing closes the dialog mid-request, so a failure brings the admin back to their note */}
      <Dialog open={applyOpen} onOpenChange={next => !apply.isPending && setApplyOpen(next)} size="sm">
        <Dialog.Header>
          <Dialog.Title className="text-lg font-bold font-sans">Apply this code as a new version</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="flex flex-col gap-4">
          {pkg.isGlobal ? (
            <p className="text-sm text-slate-600">This replaces the current version right away.</p>
          ) : (
            <RadioGroup
              aria-label="How to apply"
              value={approveNow ? "now" : "send"}
              onValueChange={next => setApproveNow(next === "now")}
              className="gap-3"
            >
              {(
                [
                  {
                    value: "send",
                    label: "Send to the coach to approve",
                    help: "It shows up for them as a new version to keep or discard.",
                  },
                  {
                    value: "now",
                    label: "Approve now",
                    help: "Replaces the current version right away. Published designs update for players.",
                  },
                ] as const
              ).map(choice => (
                <RadioGroup.Button
                  key={choice.value}
                  value={choice.value}
                  aria-label={choice.label}
                  className="mt-1 self-start"
                  label={
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-slate-900">{choice.label}</span>
                      <span className="text-xs text-slate-500">{choice.help}</span>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          )}
          <Field label="What changed (optional)" description="The coach sees this in the design's activity.">
            {controlProps => (
              <Textarea
                {...controlProps}
                value={note}
                maxLength={MAX_NOTE_LENGTH}
                minRows={2}
                autoSize
                onChange={event => setNote(event.target.value)}
              />
            )}
          </Field>
        </Dialog.Content>
        <Dialog.Footer>
          <Button variant="ghost" onClick={() => setApplyOpen(false)} disabled={apply.isPending}>
            Cancel
          </Button>
          <Button variant="default" loading={apply.isPending} onClick={confirmApply}>
            Apply
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
};
